import { path } from 'ramda';
import { log } from 'log';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserSelector } from 'js/store/hooks';

import * as Types from '../types';
import { ReceivePacketCallback } from '../../../types';

export const useXpointSubscription = ({
    sdk,
    enable,
    license,
    transactionID,
    receivePacket
}: {
    sdk: Types.XPointSDKInstance | null;
    enable: boolean;
    license: any;
    transactionID: string | null;
    receivePacket: ReceivePacketCallback;
}) => {
    const userId: string = useUserSelector(path(['userDetails', 'userId'])) ?? '';
    const [canSubscribe, setCanSubscribe] = useState(false);
    // need to use ref to keep only initial value
    const licenseInitRef = useRef(license);
    const transactionIDInitRef = useRef(transactionID);
    // need to use ref also here to keep value change separately from const handleEvent()
    const transactionIDRef = useRef(transactionID);

    const handleEvent = useCallback(async (checkRequest: Types.XPointSDKData) => {
        log('gcRes', {
            event: 'subscription',
            transactionIDInit: transactionIDInitRef.current,
            transactionID: transactionIDRef.current,
            license: licenseInitRef.current,
            message: checkRequest.status
        });

        switch (checkRequest.status) {
            case window.xpoint!.CheckRequestStatus.ALLOWED:
            case window.xpoint!.CheckRequestStatus.DENIED:
                await receivePacket(checkRequest.jwt, transactionIDRef.current);
                break;
            case window.xpoint!.CheckRequestStatus.WAITING:
                // no need to do anything here
                break;
            case window.xpoint!.CheckRequestStatus.IDLE:
                await receivePacket('IDLE', transactionIDRef.current);

                break;
            default:
            // noop
        }
    }, [
        receivePacket,
        transactionIDInitRef,
        licenseInitRef
    ]);

    // this will run only once as soon as we have both transactionId and license
    useEffect(() => {
        if (!enable || !sdk || !userId || !transactionID || !license || licenseInitRef.current || transactionIDInitRef.current) {
            return;
        }

        // setting the first values and then setting the flag can subscribe to true
        licenseInitRef.current = license;
        transactionIDInitRef.current = transactionID;

        setCanSubscribe(true);
    }, [
        sdk,
        userId,
        enable,
        license,
        transactionID,
        licenseInitRef,
        transactionIDInitRef
    ]);

    // this will run each time transactionId changes
    useEffect(() => {
        if (!enable || !sdk || !transactionID) {
            return;
        }

        transactionIDRef.current = transactionID;
    }, [
        sdk,
        enable,
        transactionID,
        transactionIDRef
    ]);

    useEffect(() => {
        if (!enable || !sdk || !userId || !canSubscribe || !licenseInitRef.current || !transactionIDInitRef.current) {
            return;
        }

        const transactionIDLocal = transactionIDInitRef.current;
        const licenseLocal = licenseInitRef.current;

        const {
            clientId,
            clientBrand,
            jurisdictionAreaId,
            jurisdictionAreaName,
            customData,
            sessionHash
        } = Object.fromEntries(licenseLocal.map(({
            key,
            value
        }: {
            key: string;
            value: string;
        }) => {
            return [key, value];
        }) ?? []);

        log('gcInfo', {
            event: 'subscribe',
            transactionID: transactionIDLocal,
            license: licenseLocal,
            sessionHash
        });

        sdk.subscribe!({
            userId,
            client: clientId,
            clientBrand,
            customData,
            jurisdictionArea: {
                id: jurisdictionAreaId,
                name: jurisdictionAreaName
            },
            sessionId: sessionHash ?? ''
        }, handleEvent);

        return () => {
            log('gcInfo', {
                event: 'unsubscribe',
                transactionID: transactionIDLocal,
                license: licenseLocal,
                sessionHash
            });

            sdk.unsubscribe!();

            licenseInitRef.current = null;
            transactionIDInitRef.current = null;

            setCanSubscribe(false);
        };
    }, [
        sdk,
        enable,
        userId,
        licenseInitRef,
        transactionIDInitRef,
        canSubscribe,
        handleEvent
    ]);
};
